import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { MatChipsModule } from '@angular/material/chips';

import { ApiService } from '../services/api.service';
import { extractList } from '../shared/utils/extract-list';
import { AuthService } from '../core/services/auth.service';

interface AttendanceRecord {
  id: string;
  employeeId: string;
  employeeName: string;
  date: string;
  checkIn: string;
  checkOut: string;
  expectedHours: number;
  overtimeHours: number;
  lateMinutes: number;
  earlyExitMinutes: number;
  actualHours: number | null;
  isRelaxed: boolean;
  status: string;
  notes: string;
}

@Component({
  selector: 'app-attendance',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatDividerModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatTabsModule,
    MatChipsModule
  ],
  templateUrl: './attendance.component.html',
  styleUrl: './attendance.component.scss'
})
export class AttendanceComponent implements OnInit {

  loading = false;
  uploading = false;
  error: string | null = null;
  importMessage: string | null = null;

  searchText = '';
  selectedStatus = 'All';
  selectedMonth = new Date().getMonth() + 1;
  selectedYear = new Date().getFullYear();
  periodType: 'Daily' | 'Weekly' | 'Monthly' | 'Yearly' = 'Monthly';
  selectedEmployeeId = '';
  csvFile: File | null = null;

  employees: { id: string; name: string }[] = [];
  records: AttendanceRecord[] = [];
  filteredRecords: AttendanceRecord[] = [];
  summary: any = null;
  calendarDays: any[] = [];
  imports: any[] = [];
  reportTotals: any = null;

  statuses = ['All', 'Present', 'Absent', 'Late', 'Leave', 'HalfDay', 'Remote'];
  periodOptions = ['Daily', 'Weekly', 'Monthly', 'Yearly'] as const;

  displayedColumns: string[] = [
    'employee', 'date', 'checkIn', 'checkOut',
    'expectedHours', 'overtimeHours', 'lateMinutes', 'earlyExit',
    'actualHours', 'isRelaxed', 'status'
  ];

  constructor(private apiService: ApiService, private dialog: MatDialog, private auth: AuthService) {}

  get canEditReconciliation(): boolean {
    return this.auth.hasAnyRole(['hr', 'super_admin', 'tenant_admin']);
  }

  get canImport(): boolean {
    return this.auth.hasAnyRole(['hr', 'super_admin', 'tenant_admin']);
  }

  ngOnInit(): void {
    this.loadEmployees();
    this.refreshAll();
  }

  refreshAll(): void {
    if (this.canEditReconciliation) {
      this.loadReconciliation();
    } else {
      this.loadAttendanceRecords();
    }
    if (this.selectedEmployeeId) {
      this.loadSummary();
      this.loadCalendar();
      this.loadImports();
    }
  }

  loadEmployees(): void {
    this.apiService.getEmployees().subscribe({
      next: (response) => {
        const data = extractList(response);
        this.employees = data.map((e: any) => ({
          id: String(e.id ?? e.employeeId ?? ''),
          name:
            e.fullName ||
            e.displayName ||
            e.employeeName ||
            [e.firstName, e.lastName].filter(Boolean).join(' ') ||
            e.employeeNumber ||
            `Employee ${e.id}`
        })).filter((e: { id: string }) => !!e.id);

        if (!this.selectedEmployeeId && this.employees.length) {
          this.selectedEmployeeId = this.employees[0].id;
          this.loadSummary();
          this.loadCalendar();
          this.loadImports();
        }
      },
      error: () => {
        this.employees = [];
      }
    });
  }

  onEmployeeChange(): void {
    this.loadSummary();
    this.loadCalendar();
    this.loadImports();
    if (this.canEditReconciliation) {
      this.loadReconciliation();
    }
  }

  onPeriodChange(): void {
    this.loadSummary();
    this.loadCalendar();
    this.loadReport();
  }

  loadAttendanceRecords(): void {
    this.loading = true;
    this.error = null;

    this.apiService.getAttendanceRecords().subscribe({
      next: (response) => {
        const data = extractList(response);
        this.records = data.map((record: any) => this.mapRecord(record));
        this.filteredRecords = [...this.records];
        this.loading = false;
      },
      error: (err) => {
        this.error = err?.error?.message || 'Failed to load attendance.';
        this.loading = false;
      }
    });
  }

  loadSummary(): void {
    if (!this.selectedEmployeeId) return;
    const anchor = this.anchorDate();
    this.apiService.getEmployeeAttendanceSummary(this.selectedEmployeeId, {
      periodType: this.periodTypeToEnum(),
      anchorDate: anchor,
      persist: true
    }).subscribe({
      next: (res) => {
        this.summary = res?.data ?? res;
      },
      error: (err) => {
        this.summary = null;
        this.error = err?.error?.message || 'Failed to load summary.';
      }
    });
  }

  loadCalendar(): void {
    if (!this.selectedEmployeeId) return;
    const { from, to } = this.monthRange();
    this.apiService.getEmployeeAttendanceCalendar(this.selectedEmployeeId, from, to).subscribe({
      next: (res) => {
        this.calendarDays = res?.data ?? res ?? [];
      },
      error: () => {
        this.calendarDays = [];
      }
    });
  }

  loadImports(): void {
    if (!this.selectedEmployeeId || !this.canImport) {
      this.imports = [];
      return;
    }
    this.apiService.getEmployeeAttendanceImports(this.selectedEmployeeId).subscribe({
      next: (res) => {
        this.imports = res?.data ?? extractList(res) ?? [];
      },
      error: () => {
        this.imports = [];
      }
    });
  }

  loadReport(): void {
    this.apiService.getAttendanceReport(
      this.periodTypeToEnum(),
      this.anchorDate(),
      this.canImport ? undefined : this.selectedEmployeeId || undefined
    ).subscribe({
      next: (res) => {
        const data = res?.data ?? res;
        this.reportTotals = data?.totals ?? null;
      },
      error: () => {
        this.reportTotals = null;
      }
    });
  }

  onCsvPicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.csvFile = input.files?.[0] || null;
    input.value = '';
  }

  exportAttendanceCsv(): void {
    if (!this.selectedEmployeeId) {
      this.error = 'Select an employee before exporting CSV.';
      return;
    }
    const { from, to } = this.monthRange();
    const emp = this.employees.find((e) => e.id === this.selectedEmployeeId);
    const label = (emp?.name || this.selectedEmployeeId).replace(/\s+/g, '_');
    this.apiService.exportEmployeeAttendanceCsv(this.selectedEmployeeId, from, to).subscribe({
      next: (blob) => {
        this.downloadBlob(
          blob,
          `attendance_${label}_${this.selectedYear}-${String(this.selectedMonth).padStart(2, '0')}.csv`
        );
        this.importMessage = `Exported ${emp?.name || 'employee'} for ${this.selectedMonth}/${this.selectedYear}.`;
        this.error = null;
      },
      error: (err) => {
        this.error = err?.error?.message || err?.message || 'Failed to export attendance CSV.';
      }
    });
  }

  downloadAttendanceTemplate(): void {
    const csv = [
      'Date,CheckIn,CheckOut,Status,Notes,Location',
      `${this.formatMonthStart(this.selectedYear, this.selectedMonth)},09:00,18:00,Present,Regular day,Office`
    ].join('\n');
    this.downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), 'attendance_monthly_template.csv');
    this.importMessage = 'Downloaded monthly attendance CSV template (one employee).';
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  uploadCsv(): void {
    if (!this.canImport) return;
    if (!this.selectedEmployeeId) {
      this.error = 'Select an employee before uploading CSV.';
      return;
    }
    if (!this.csvFile) {
      this.error = 'Choose a CSV file.';
      return;
    }

    const { from, to } = this.monthRange();
    this.uploading = true;
    this.error = null;
    this.importMessage = null;
    this.apiService.importEmployeeAttendanceCsv(this.selectedEmployeeId, this.csvFile, from, to).subscribe({
      next: (res) => {
        this.uploading = false;
        this.csvFile = null;
        const data = res?.data ?? res;
        this.importMessage =
          `Imported ${data?.importedRows ?? 0} · skipped duplicates ${data?.duplicateRows ?? 0} · invalid ${data?.invalidRows ?? 0}` +
          ` · month ${this.selectedMonth}/${this.selectedYear}`;
        if (data?.errorSummary) {
          this.importMessage += ` · ${data.errorSummary}`;
        }
        this.loadImports();
        this.loadSummary();
        this.loadCalendar();
        this.refresh();
      },
      error: (err) => {
        this.uploading = false;
        this.error = err?.error?.message || err?.message || 'CSV import failed.';
      }
    });
  }

  filterRecords(): void {
    const search = this.searchText.toLowerCase().trim();

    this.filteredRecords = this.records.filter(record => {
      const matchesSearch = record.employeeName.toLowerCase().includes(search) ||
        record.date.includes(search);
      const matchesStatus = this.selectedStatus === 'All' || record.status === this.selectedStatus;
      const matchesEmployee = !this.selectedEmployeeId || record.employeeId === this.selectedEmployeeId;
      return matchesSearch && matchesStatus && matchesEmployee;
    });
  }

  clearFilters(): void {
    this.searchText = '';
    this.selectedStatus = 'All';
    this.filteredRecords = [...this.records];
  }

  refresh(): void {
    if (this.canEditReconciliation) this.loadReconciliation();
    else this.loadAttendanceRecords();
    this.loadSummary();
    this.loadCalendar();
    this.loadReport();
  }

  loadReconciliation(): void {
    this.loading = true;
    this.error = null;

    const from = this.formatMonthStart(this.selectedYear, this.selectedMonth);
    const to = this.formatMonthEnd(this.selectedYear, this.selectedMonth);

    this.apiService.getAttendanceReconciliation(
      from,
      to,
      this.selectedEmployeeId || undefined
    ).subscribe({
      next: (response) => {
        const data = extractList(response);
        this.records = data.map((row: any) => this.mapRecord(row));
        this.filterRecords();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.records = [];
        this.filteredRecords = [];
      }
    });
  }

  saveReconciliation(): void {
    if (!this.canEditReconciliation) return;

    const items = this.filteredRecords.map((r) => ({
      attendanceRecordId: r.id,
      actualWorkedHours: r.actualHours,
      isRelaxed: r.isRelaxed
    }));

    this.apiService.updateAttendanceReconciliation(items).subscribe({
      next: () => this.loadReconciliation(),
      error: (error) => {
        this.error = error?.error?.message || 'Failed to save reconciliation.';
      }
    });
  }

  dayClass(day: any): string {
    const classes = ['cal-day'];
    if (day.isWeekend) classes.push('weekend');
    if (day.isHoliday) classes.push('holiday');
    if (day.onLeave) classes.push('leave');
    const status = this.normalizeAttendanceStatus(day.status);
    if (status === 'Present') classes.push('present');
    if (status === 'Absent') classes.push('absent');
    if (status === 'Late') classes.push('late');
    if (day.isEarlyExit) classes.push('early');
    return classes.join(' ');
  }

  normalizeStatusLabel(status: unknown): string {
    return this.normalizeAttendanceStatus(status).charAt(0);
  }

  private mapRecord(record: any): AttendanceRecord {
    return {
      id: String(record.id ?? ''),
      employeeId: String(record.employeeId ?? ''),
      employeeName:
        record.employeeName ||
        record.employee?.displayName ||
        record.employee?.name ||
        `Employee #${record.employeeId || 'N/A'}`,
      date: record.date || record.attendanceDate || '',
      checkIn: record.checkInAt
        ? String(record.checkInAt).slice(11, 16)
        : (record.checkIn || '—'),
      checkOut: record.checkOutAt
        ? String(record.checkOutAt).slice(11, 16)
        : (record.checkOut || '—'),
      expectedHours: Number(record.workedHours ?? record.totalHours ?? record.hours ?? 0),
      overtimeHours: Number(record.overtimeHours ?? 0),
      lateMinutes: Number(record.lateMinutes ?? 0),
      earlyExitMinutes: Number(record.earlyExitMinutes ?? 0),
      actualHours: record.actualWorkedHours != null ? Number(record.actualWorkedHours) : null,
      isRelaxed: Boolean(record.isRelaxed ?? false),
      status: this.normalizeAttendanceStatus(record.status ?? record.attendanceStatus),
      notes: record.notes || ''
    };
  }

  private periodTypeToEnum(): number {
    switch (this.periodType) {
      case 'Daily': return 0;
      case 'Weekly': return 1;
      case 'Monthly': return 2;
      case 'Yearly': return 3;
      default: return 2;
    }
  }

  private anchorDate(): string {
    if (this.periodType === 'Yearly') {
      return `${this.selectedYear}-01-01`;
    }
    return this.formatMonthStart(this.selectedYear, this.selectedMonth);
  }

  private monthRange(): { from: string; to: string } {
    return {
      from: this.formatMonthStart(this.selectedYear, this.selectedMonth),
      to: this.formatMonthEnd(this.selectedYear, this.selectedMonth)
    };
  }

  private normalizeAttendanceStatus(status: unknown): string {
    if (typeof status === 'number') {
      const labels = ['Present', 'Absent', 'Late', 'HalfDay', 'Leave', 'Remote'];
      return labels[status] ?? 'Present';
    }
    const raw = String(status ?? '');
    if (raw === 'OnLeave') return 'Leave';
    return raw || 'Present';
  }

  private formatMonthStart(year: number, month: number): string {
    const mm = String(month).padStart(2, '0');
    return `${year}-${mm}-01`;
  }

  private formatMonthEnd(year: number, month: number): string {
    const lastDay = new Date(year, month, 0).getDate();
    const mm = String(month).padStart(2, '0');
    return `${year}-${mm}-${String(lastDay).padStart(2, '0')}`;
  }
}
